import { useState, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDailyCelebration } from '@/hooks/useDailyCelebration';
import { PartnerModal } from '@/components/PartnerModal';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface DailyCelebrationProps {
  groupId: string | undefined;
  isDemo?: boolean;
}

export function DailyCelebration({ groupId, isDemo = false }: DailyCelebrationProps) {
  const { language, t } = useLanguage();
  const { todayCelebrations, todayMeniny, todayBirthdays, nearestBirthdays, isLoading } = useDailyCelebration(groupId, isDemo);
  const [partnerModal, setPartnerModal] = useState<{ partnerId: string; milestoneDays: number } | null>(null);

  // Fetch partner data if needed (skip in demo)
  const partnerIds = useMemo(
    () => isDemo ? [] : todayCelebrations.filter(c => c.partner_id).map(c => c.partner_id!),
    [todayCelebrations, isDemo]
  );
  const { data: partners = [] } = useQuery({
    queryKey: ['celebration-partners', partnerIds],
    queryFn: async () => {
      if (partnerIds.length === 0) return [];
      const { data } = await supabase
        .from('partners')
        .select('*')
        .in('id', partnerIds);
      return data || [];
    },
    enabled: partnerIds.length > 0,
    staleTime: 1000 * 60 * 60 * 6,
  });

  if (isLoading) return null;

  const hasCelebrations = todayCelebrations.length > 0;
  const hasMeniny = !hasCelebrations && language === 'sk' && todayMeniny.length > 0;
  const hasBirthdays = todayBirthdays.length > 0 || nearestBirthdays.length > 0;

  // Nothing to show
  if (!hasCelebrations && !hasMeniny && !hasBirthdays) return null;

  const partnerMap = new Map(partners.map((p: any) => [p.id, p]));

  return (
    <>
      <div className="w-full space-y-1 text-center">
        <p className="text-sm font-semibold text-muted-foreground mb-1">
          {t('dailyCelebrationHeadline')}
        </p>

        {/* Celebration days (non-meniny) */}
        {todayCelebrations.map((c) => {
          const title = language === 'sk' ? c.title_sk : (c.title_en || c.title_sk);
          const partner = c.partner_id ? partnerMap.get(c.partner_id) : null;

          return (
            <p key={c.id} className="text-sm text-muted-foreground/90">
              🌍{' '}
              {partner ? (
                <button
                  onClick={() => setPartnerModal({ partnerId: c.partner_id!, milestoneDays: 0 })}
                  className="underline underline-offset-2 hover:text-foreground transition-colors"
                >
                  {title}
                </button>
              ) : c.partner_id ? (
                // partner_id set but partner not found — render plain text
                title
              ) : (
                title
              )}
            </p>
          );
        })}

        {/* Meniny from DB (SK only, only if no celebrations) */}
        {hasMeniny && todayMeniny.map((m) => (
          <p key={m.id} className="text-sm text-muted-foreground/90">
            🇸🇰 {m.title_sk.replace('Meniny: ', 'Meniny má ')}
          </p>
        ))}

        {/* Birthday block */}
        {todayBirthdays.length > 0 ? (
          <p className="text-sm text-muted-foreground/90">
            🎂{' '}
            {todayBirthdays.length === 1
              ? `${t('birthdayToday')} ${todayBirthdays[0].display_name}`
              : `${t('birthdayTodayPlural')} ${todayBirthdays.map((b) => b.display_name).join(', ')}`}
          </p>
        ) : nearestBirthdays.length > 0 ? (
          <p className="text-sm text-muted-foreground/90">
            🎂 {t('birthdayNearest')} {nearestBirthdays.map(b => b.display_name).join(', ')} {nearestBirthdays[0].dateLabel}
          </p>
        ) : null}
      </div>

      {/* Partner modal */}
      {partnerModal && partnerMap.get(partnerModal.partnerId) && (
        <PartnerModal
          partner={partnerMap.get(partnerModal.partnerId)!}
          milestoneDays={partnerModal.milestoneDays}
          onClose={() => setPartnerModal(null)}
        />
      )}
    </>
  );
}
